import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import ProductCard from "@/components/feature/ProductCard";
import ImageLightbox from "@/components/feature/ImageLightbox";
import useSEO from "@/hooks/useSEO";
import { products } from "@/mocks/products";

export default function ProductDetail() {
  const { id } = useParams<{ id: string }>();
  const product = products.find((p) => p.id === id);
  const [activeImage, setActiveImage] = useState(0);
  const [lightbox, setLightbox] = useState<string | null>(null);

  useSEO({
    title: product
      ? `${product.name} - 糖心VLGO产品详情`
      : "产品不存在 - 糖心VLGO",
    description: product
      ? `${product.name}：${product.summary} 糖心VLGO 官方产品介绍与规格说明。`
      : "未找到对应的糖心VLGO产品信息。",
    keywords: product
      ? `${product.name},糖心VLGO产品,${product.series},糖心VLGO`
      : "糖心VLGO产品",
  });

  const related = useMemo(() => {
    if (!product) return [];
    const same = products.filter((p) => p.id !== product.id && p.series === product.series);
    const others = products.filter((p) => p.id !== product.id && p.series !== product.series);
    return [...same, ...others].slice(0, 3);
  }, [product]);

  if (!product) {
    return (
      <SiteLayout>
        <div className="w-full min-h-[70vh] flex flex-col items-center justify-center text-center px-4 pt-24">
          <span className="w-20 h-20 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-emotion-sad-line text-4xl leading-none"></i>
          </span>
          <h1 className="mt-6 font-heading text-2xl md:text-3xl font-extrabold text-foreground-950">
            产品不存在
          </h1>
          <p className="mt-3 text-sm text-foreground-600">
            抱歉，没有找到对应的产品信息，可能链接已失效。
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Link
              to="/news"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 font-semibold text-sm whitespace-nowrap cursor-pointer transition-colors hover:bg-primary-600"
            >
              <i className="ri-apps-line leading-none"></i>
              浏览全部产品
            </Link>
            <Link
              to="/"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full border border-background-300 text-foreground-700 font-semibold text-sm whitespace-nowrap cursor-pointer transition-colors hover:bg-background-100"
            >
              返回首页
            </Link>
          </div>
        </div>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout>
      {/* 顶部留白 + 面包屑 */}
      <div className="w-full px-4 md:px-10 pt-28 pb-6 bg-background-50">
        <nav className="flex items-center gap-2 text-sm text-foreground-500 flex-wrap">
          <Link to="/" className="hover:text-primary-600 transition-colors">
            首页
          </Link>
          <i className="ri-arrow-right-s-line leading-none"></i>
          <Link to="/news" className="hover:text-primary-600 transition-colors">
            产品资讯
          </Link>
          <i className="ri-arrow-right-s-line leading-none"></i>
          <span className="text-foreground-900">{product.name}</span>
        </nav>
      </div>

      {/* 主图 + 基本信息 */}
      <section className="w-full px-4 md:px-10 pb-12 bg-background-50">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14">
          <div>
            <div
              className="relative w-full h-[360px] md:h-[520px] rounded-lg overflow-hidden bg-background-100 cursor-zoom-in"
              onClick={() => setLightbox(product.gallery[activeImage])}
            >
              <img
                src={product.gallery[activeImage]}
                alt={`${product.name} 主图`}
                title={`${product.name} - 糖心VLGO`}
                className="w-full h-full object-cover object-top"
              />
              <span className="absolute bottom-4 right-4 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-foreground-950/55 text-background-50 text-xs backdrop-blur-sm">
                <i className="ri-zoom-in-line leading-none"></i>
                点击放大
              </span>
            </div>
            <div className="mt-4 grid grid-cols-4 gap-3">
              {product.gallery.map((img, idx) => (
                <button
                  key={img}
                  type="button"
                  onClick={() => setActiveImage(idx)}
                  className={`relative w-full h-20 md:h-24 rounded-lg overflow-hidden bg-background-100 cursor-pointer border-2 transition-colors ${
                    activeImage === idx ? "border-primary-500" : "border-transparent"
                  }`}
                >
                  <img
                    src={img}
                    alt={`${product.name} 预览图 ${idx + 1}`}
                    className="w-full h-full object-cover object-top"
                  />
                </button>
              ))}
            </div>
          </div>

          <div>
            <span className="inline-flex px-3 py-1 rounded-full bg-accent-100 text-accent-800 text-xs font-medium">
              {product.series}
            </span>
            <h1 className="mt-4 font-heading text-2xl md:text-4xl font-extrabold text-foreground-950 tracking-tight">
              {product.name}
            </h1>
            <p className="mt-2 text-base text-accent-600 font-medium">{product.tagline}</p>
            <div className="mt-5 flex items-end gap-4">
              <span className="font-heading text-3xl font-extrabold text-primary-600">{product.price}</span>
              <span className="text-sm text-foreground-500 mb-1">{product.weight}</span>
            </div>
            <p className="mt-6 text-sm md:text-base text-foreground-600 leading-relaxed">
              {product.summary}
            </p>

            <div className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {product.features.map((f) => (
                <div key={f.title} className="flex items-start gap-3 rounded-lg bg-background-100 p-4">
                  <span className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-500 text-background-50 shrink-0">
                    <i className="ri-check-line text-lg leading-none"></i>
                  </span>
                  <div>
                    <h3 className="text-sm font-bold text-foreground-950">{f.title}</h3>
                    <p className="mt-1 text-xs text-foreground-600 leading-relaxed">{f.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-7 flex flex-col sm:flex-row gap-3">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-primary-500 text-background-50 font-semibold whitespace-nowrap cursor-pointer transition-colors hover:bg-primary-600"
              >
                <i className="ri-customer-service-2-line leading-none"></i>
                咨询这款产品
              </Link>
              <Link
                to="/news"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full border border-background-300 text-foreground-700 font-semibold whitespace-nowrap cursor-pointer transition-colors hover:bg-background-100"
              >
                返回产品资讯
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* 产品介绍 */}
      <section className="w-full bg-secondary-50">
        <div className="w-full px-4 md:px-10 py-16 md:py-20 grid grid-cols-1 lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2">
            <h2 className="font-heading text-2xl md:text-3xl font-extrabold text-foreground-950 tracking-tight">
              产品介绍
            </h2>
            <div className="mt-5 flex flex-col gap-4 text-sm md:text-base text-foreground-600 leading-relaxed">
              {product.description.map((p) => (
                <p key={p}>{p}</p>
              ))}
            </div>

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div className="rounded-lg bg-background-50 border border-background-200 p-6">
                <div className="flex items-center gap-2">
                  <span className="w-8 h-8 flex items-center justify-center rounded-full bg-accent-500 text-background-50">
                    <i className="ri-guide-line text-base leading-none"></i>
                  </span>
                  <h3 className="font-heading text-base font-bold text-foreground-950">使用说明</h3>
                </div>
                <ul className="mt-4 flex flex-col gap-2.5">
                  {product.usage.map((u) => (
                    <li key={u} className="flex items-start gap-2 text-sm text-foreground-600 leading-relaxed">
                      <i className="ri-checkbox-circle-line text-primary-500 mt-0.5 leading-none"></i>
                      {u}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="rounded-lg bg-background-50 border border-background-200 p-6">
                <div className="flex items-center gap-2">
                  <span className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-500 text-background-50">
                    <i className="ri-map-pin-2-line text-base leading-none"></i>
                  </span>
                  <h3 className="font-heading text-base font-bold text-foreground-950">适用场景</h3>
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  {product.scenes.map((s) => (
                    <span
                      key={s}
                      className="px-3 py-1.5 rounded-full bg-secondary-100 text-secondary-900 text-xs font-medium"
                    >
                      {s}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div>
            <div className="rounded-lg bg-background-50 border border-background-200 p-6">
              <h2 className="font-heading text-lg font-bold text-foreground-950">产品规格</h2>
              <dl className="mt-4 flex flex-col divide-y divide-background-200">
                {product.specs.map((s) => (
                  <div key={s.label} className="flex flex-col gap-1 py-3">
                    <dt className="text-xs text-foreground-500">{s.label}</dt>
                    <dd className="text-sm text-foreground-900">{s.value}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>
        </div>
      </section>

      {/* 相关推荐 */}
      <section className="w-full px-4 md:px-10 py-16 md:py-20 bg-background-50">
        <div className="flex items-end justify-between gap-4">
          <div>
            <span className="inline-flex items-center gap-2 text-sm font-semibold text-accent-600">
              <span className="w-6 h-[2px] bg-accent-500"></span>
              相关推荐
            </span>
            <h2 className="mt-3 font-heading text-2xl md:text-3xl font-extrabold text-foreground-950 tracking-tight">
              你可能还喜欢
            </h2>
          </div>
          <Link
            to="/news"
            className="hidden sm:inline-flex items-center gap-2 text-sm font-semibold text-primary-600 whitespace-nowrap cursor-pointer hover:text-primary-700"
          >
            查看全部
            <i className="ri-arrow-right-line leading-none"></i>
          </Link>
        </div>
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {related.map((p) => (
            <ProductCard key={p.id} product={p} />
          ))}
        </div>
      </section>

      {lightbox && (
        <ImageLightbox src={lightbox} alt={`${product.name} 大图预览`} onClose={() => setLightbox(null)} />
      )}
    </SiteLayout>
  );
}